import { Form } from 'antd';
import { useMemo, useRef, useState } from 'react';
import type {
  AdminProduct,
  AdminProductFormValues,
  MaterialIconOption,
  ProductTypeOption,
  SizeOption,
} from '../interfaces';
import { getFilteredSizes } from './adminProductForm.utils';

export const useAdminProductFormState = () => {
  const [form] = Form.useForm<AdminProductFormValues>();
  const [types, setTypes] = useState<ProductTypeOption[]>([]);
  const [sizes, setSizes] = useState<SizeOption[]>([]);
  const [materialTemplates, setMaterialTemplates] = useState<MaterialIconOption[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [products, setProducts] = useState<AdminProduct[]>([]);
  const [baseProducts, setBaseProducts] = useState<AdminProduct[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [loadingMoreProducts, setLoadingMoreProducts] = useState(false);
  const [savingStockId, setSavingStockId] = useState<string | null>(null);
  const [savingPriceId, setSavingPriceId] = useState<string | null>(null);
  const [savingSizesId, setSavingSizesId] = useState<string | null>(null);
  const [loadingMaterialDetailsById, setLoadingMaterialDetailsById] = useState<Record<string, boolean>>({});
  const [deletingProductId, setDeletingProductId] = useState<string | null>(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [draftStockById, setDraftStockById] = useState<Record<string, number>>({});
  const [draftPriceById, setDraftPriceById] = useState<Record<string, number>>({});
  const [currentProductsPage, setCurrentProductsPage] = useState(1);
  const [productsSkip, setProductsSkip] = useState(0);
  const [productsHasMore, setProductsHasMore] = useState(true);
  const [productsTotal, setProductsTotal] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSearchTerm, setActiveSearchTerm] = useState('');
  const [searchingProducts, setSearchingProducts] = useState(false);
  const createCardRef = useRef<HTMLDivElement | null>(null);
  const selectedGender = Form.useWatch('gender', form);
  const selectedProductTypeId = Form.useWatch('productTypeId', form);

  const selectedTypeName = useMemo(
    () => types.find((type) => type.id === selectedProductTypeId)?.name || '',
    [selectedProductTypeId, types]
  );
  const selectedTypeRequiresSizes = useMemo(
    () => types.find((type) => type.id === selectedProductTypeId)?.requiresSizes ?? true,
    [selectedProductTypeId, types]
  );

  const filteredSizes = useMemo(
    () => getFilteredSizes(sizes, selectedGender, selectedTypeName, selectedTypeRequiresSizes),
    [selectedGender, selectedTypeName, selectedTypeRequiresSizes, sizes]
  );

  return {
    form,
    types,
    sizes,
    materialTemplates,
    loadingOptions,
    submitting,
    products,
    baseProducts,
    loadingProducts,
    loadingMoreProducts,
    savingStockId,
    savingPriceId,
    savingSizesId,
    loadingMaterialDetailsById,
    deletingProductId,
    isCreateOpen,
    draftStockById,
    draftPriceById,
    currentProductsPage,
    productsSkip,
    productsHasMore,
    productsTotal,
    searchTerm,
    activeSearchTerm,
    searchingProducts,
    createCardRef,
    filteredSizes,
    selectedTypeRequiresSizes,
    setTypes,
    setSizes,
    setMaterialTemplates,
    setLoadingOptions,
    setSubmitting,
    setProducts,
    setBaseProducts,
    setLoadingProducts,
    setLoadingMoreProducts,
    setSavingStockId,
    setSavingPriceId,
    setSavingSizesId,
    setLoadingMaterialDetailsById,
    setDeletingProductId,
    setIsCreateOpen,
    setDraftStockById,
    setDraftPriceById,
    setCurrentProductsPage,
    setProductsSkip,
    setProductsHasMore,
    setProductsTotal,
    setSearchTerm,
    setActiveSearchTerm,
    setSearchingProducts,
  };
};
